import { useEffect } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
  faRotateRight,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import ModalPortal from "./modal-portal";

export type ConfirmTone = "danger" | "warning" | "primary";

const toneStyles: Record<
  ConfirmTone,
  { icon: IconDefinition; chip: string; confirm: string }
> = {
  danger: {
    icon: faTrash,
    chip: "bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300",
    confirm:
      "bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-500/20",
  },
  warning: {
    icon: faTriangleExclamation,
    chip: "bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300",
    confirm:
      "bg-amber-500 hover:bg-amber-400 text-white shadow-lg shadow-amber-500/20",
  },
  primary: {
    icon: faRotateRight,
    chip: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300",
    confirm:
      "bg-gradient-to-r from-accent to-accent-bright text-white shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm",
  },
};

export interface ConfirmDialogProps {
  title: string;
  description?: string;
  /** Optional subject line — a file name, reference, etc. */
  detail?: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  icon?: IconDefinition;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog = ({
  title,
  description,
  detail,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "danger",
  icon,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const styles = toneStyles[tone];

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [loading, onCancel]);

  return (
    <ModalPortal>
      <div
        className="z-[99999] fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center px-4 animate-fade-in"
        onClick={() => !loading && onCancel()}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="w-full max-w-[440px] bg-surface border border-edge rounded-2xl shadow-overlay p-6 smallTablet:max-h-[90dvh] smallTablet:overflow-y-auto mobile:p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-4">
            <div
              className={clsx(
                "h-11 w-11 rounded-xl flex items-center justify-center shrink-0",
                styles.chip
              )}
            >
              <FontAwesomeIcon
                icon={icon ?? styles.icon}
                className="text-[16px]"
              />
            </div>

            <div className="min-w-0">
              <h2 className="text-[18px] font-bold text-text-primary leading-tight">
                {title}
              </h2>
              {description ? (
                <p className="mt-1.5 text-[13px] font-medium text-light-gray leading-relaxed">
                  {description}
                </p>
              ) : null}
              {detail ? (
                <p className="mt-3 max-w-full truncate rounded-lg bg-surface-muted border border-edge-faint px-3 py-1.5 text-[12px] font-semibold text-text-primary">
                  {detail}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2.5 mt-6 mobile:flex-col-reverse mobile:items-stretch">
            <button
              type="button"
              disabled={loading}
              onClick={onCancel}
              className="h-10 px-5 rounded-full border border-edge mobile:w-full text-[13px] font-semibold text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={onConfirm}
              className={clsx(
                "h-10 min-w-[128px] px-5 rounded-full flex mobile:w-full items-center justify-center text-[13px] font-semibold transition-all duration-200 disabled:opacity-70 disabled:cursor-wait",
                styles.confirm
              )}
            >
              {loading ? (
                <FontAwesomeIcon
                  icon={faCircleNotch}
                  className="fa-spin text-[14px]"
                />
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ConfirmDialog;
